import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { todayMSKString } from '../utils/timezone';

const CELL_STYLE = {
  current: { background: '#DCF4E0', borderColor: '#A5D6A7' },
  planned: { background: '#DEEFFF', borderColor: '#90CAF9' },
  deficit: { background: '#F9EBCF', borderColor: '#E4C177' },
  gap:     { background: '#FFE6E3', borderColor: '#FFAEA4' },
  past:    { background: '#F4F4EF', borderColor: '#D8DDDA' },
};

function formatDateHeader(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '');
}

function isToday(dateStr) {
  return dateStr === todayMSKString();
}

function CoverageCell({ cell, isToday }) {
  if (!cell) return <td style={{ padding: '6px 8px', border: '1px solid #eee', background: '#F9F9F6', textAlign: 'center', minWidth: 60 }} />;

  const { status, current_stock, planned_stock, available } = cell;

  // Диагональный фон когда есть и склад и поставка одновременно
  const hasBoth = current_stock > 0 && planned_stock > 0;
  const baseStyle = hasBoth
    ? { background: 'linear-gradient(135deg, #43A047 50%, #1E88E5 50%)', borderColor: '#1F763B' }
    : CELL_STYLE[status] || CELL_STYLE.gap;

  let display, textColor, fontWeight;

  if (status === 'gap' || status === 'past') {
    display = '—';
    textColor = status === 'gap' ? '#CB4D45' : '#bbb';
    fontWeight = 700;
  } else if (status === 'deficit') {
    display = available;
    textColor = '#CB4D45';
    fontWeight = 700;
  } else {
    display = available;
    textColor = hasBoth ? '#fff' : (current_stock > 0 ? '#1F763B' : '#26689A');
    fontWeight = 600;
  }

  return (
    <td style={{
      padding: '4px 6px',
      border: `1px solid ${baseStyle.borderColor}`,
      background: baseStyle.background,
      textAlign: 'center',
      minWidth: 60,
      verticalAlign: 'middle',
      outline: isToday ? '2px solid var(--primary-color)' : 'none',
      outlineOffset: '-2px',
    }}>
      <span style={{ fontSize: 13, fontWeight, color: textColor }}>{display}</span>
    </td>
  );
}

function StockCoveragePage({ showToast }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(14);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const result = await api.getStockCoverage(days);
      setData(result);
    } catch (e) {
      showToast('Ошибка загрузки матрицы покрытия', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Загрузка...</div>;
  }

  if (!data) return null;

  const { dates, products } = data;
  const todayStr = todayMSKString();

  // Подсчёт пробелов и дефицитов для сводки
  const gapCount = products.reduce((acc, p) => {
    return acc + dates.filter(d => d >= todayStr && p.coverage[d]?.status === 'gap').length;
  }, 0);
  const productsWithGaps = products.filter(p =>
    dates.some(d => d >= todayStr && p.coverage[d]?.status === 'gap')
  );

  const deficitCount = products.reduce((acc, p) => {
    return acc + dates.filter(d => d >= todayStr && p.coverage[d]?.status === 'deficit').length;
  }, 0);
  const productsWithDeficit = products.filter(p =>
    dates.some(d => d >= todayStr && p.coverage[d]?.status === 'deficit')
  );

  return (
    <div>
      {/* Заголовок и управление */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        {/* Горизонт — общий переключатель .seg (UX-аудит 25.10) */}
        <div className="seg">
          {[14, 21, 30].map(d => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`seg-btn${days === d ? ' on' : ''}`}
            >
              {d} дней
            </button>
          ))}
        </div>
        <button onClick={loadData} className="btn btn-secondary btn-sm">Обновить</button>
        {gapCount > 0 && (
          <div style={{
            background: '#FFE6E3', border: '1px solid #FFAEA4',
            '--ch': '6px', '--stroke': '#FFAEA4', padding: '6px 14px', fontSize: 13, color: '#CB4D45',
          }}>
            ⚠ Пробелов: {gapCount} ячеек у {productsWithGaps.length} позиций
          </div>
        )}
        {deficitCount > 0 && (
          <div style={{
            background: '#F9EBCF', border: '1px solid #E4C177',
            '--ch': '6px', '--stroke': '#E4C177', padding: '6px 14px', fontSize: 13, color: '#9A4820',
          }}>
            ⚠ Дефицит: {deficitCount} ячеек у {productsWithDeficit.length} позиций
          </div>
        )}
      </div>

      {/* Легенда */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 12, flexWrap: 'wrap' }}>
        {[
          { status: 'current', label: 'В наличии' },
          { status: 'planned', label: 'Плановая поставка' },
          { status: 'deficit', label: 'Дефицит — заказано больше' },
          { status: 'gap',     label: 'Пробел — нет товара' },
        ].map(({ status, label }) => (
          <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 16, height: 16, borderRadius: 3,
              background: CELL_STYLE[status].background,
              border: `1px solid ${CELL_STYLE[status].borderColor}`,
            }} />
            <span style={{ color: '#555' }}>{label}</span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 16, height: 16, borderRadius: 3,
            background: 'linear-gradient(135deg, #43A047 50%, #1E88E5 50%)',
            border: '1px solid #1F763B',
          }} />
          <span style={{ color: '#555' }}>Склад + поставка</span>
        </div>
      </div>

      {/* Матрица */}
      <div style={{ overflowX: 'auto', '--ch': '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
          <thead>
            <tr style={{ background: '#F9F9F6' }}>
              <th style={{
                padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600,
                color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px',
                position: 'sticky', left: 0, background: '#F9F9F6', zIndex: 2,
                borderBottom: '2px solid #D8DDDA', minWidth: 160,
              }}>
                Товар
              </th>
              {dates.map(d => (
                <th key={d} style={{
                  padding: '8px 4px', textAlign: 'center', fontSize: 11,
                  fontWeight: isToday(d) ? 700 : 500,
                  color: isToday(d) ? '#1C3D2E' : '#888',
                  borderBottom: `2px solid ${isToday(d) ? 'var(--primary-color)' : '#D8DDDA'}`,
                  minWidth: 64, whiteSpace: 'nowrap',
                }}>
                  {formatDateHeader(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((product, i) => {
              const hasGap = dates.some(d => d >= todayStr &&
                (product.coverage[d]?.status === 'gap' || product.coverage[d]?.status === 'deficit')
              );
              return (
                <tr key={product.id} style={{ background: i % 2 === 0 ? '#fff' : '#F9F9F6' }}>
                  <td style={{
                    padding: '8px 16px', fontSize: 13, fontWeight: hasGap ? 600 : 400,
                    position: 'sticky', left: 0, zIndex: 1,
                    background: i % 2 === 0 ? '#fff' : '#F9F9F6',
                    borderRight: '2px solid #D8DDDA',
                    color: hasGap ? '#CB4D45' : '#1C3D2E',
                    whiteSpace: 'nowrap',
                  }}>
                    {hasGap && <span style={{ marginRight: 4 }}>⚠</span>}
                    {product.name}
                    {product.shelf_life_days && (
                      <span style={{ fontSize: 10, color: '#999', marginLeft: 4 }}>
                        ({product.shelf_life_days}д)
                      </span>
                    )}
                  </td>
                  {dates.map(d => (
                    <CoverageCell key={d} cell={product.coverage[d]} isToday={isToday(d)} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {products.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          Нет активных товаров
        </div>
      )}
    </div>
  );
}

export default StockCoveragePage;
